import { useState, useEffect } from 'react';
import { gateSessionsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const STORAGE_KEY = 'currentGateSessionId';
const TERMINAL_STATUSES = ['completed', 'cancelled'];

// An operator runs exactly one gate at a time (CR 4.1) — once they've entered
// gate + flight on the Session tab, every other operator page should reuse that
// gate + flight context automatically instead of asking again, right up until they
// log out or the session ends.
//
// Primary source: the session id the gate queue page (AdminGateQueuePage) stamps into localStorage the
// moment the operator lands on it — this covers the session from the instant it's
// found/created, before they've even pressed Acknowledge. (The /gate-sessions/active
// list alone isn't enough here: the backend only attributes an unclaimed
// auto-created session to created_by/acknowledged_by, neither of which is set for
// this operator pre-Acknowledge, even though they're the one looking at it.)
// Fallback: the /active list's own "mine" sessions, for cases where localStorage
// is empty (different device/browser) but the operator has already claimed one.
export function useMyActiveGateSession() {
  const { user } = useAuth();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.role !== 'gate_operator') {
      setLoading(false);
      return;
    }
    let cancelled = false;

    async function resolve() {
      const storedId = localStorage.getItem(STORAGE_KEY);
      if (storedId) {
        try {
          const res = await gateSessionsAPI.getById(storedId);
          const s = res.data.data;
          if (!cancelled && s && !TERMINAL_STATUSES.includes(s.status)) {
            setSession(s);
            return;
          }
          if (TERMINAL_STATUSES.includes(s?.status)) localStorage.removeItem(STORAGE_KEY);
        } catch {
          // Gone, denied, or otherwise unusable — fall through to the /active lookup.
          localStorage.removeItem(STORAGE_KEY);
        }
      }

      try {
        const res = await gateSessionsAPI.getActive();
        if (cancelled) return;
        const mine = (res.data.data || []).filter(
          s => s.created_by === user.id || s.acknowledged_by === user.id || s.operator?.id === user.id
        );
        if (mine.length > 0) {
          setSession(mine.reduce((a, b) => (b.id > a.id ? b : a)));
        }
      } catch {
        // Leave session null — callers fall back to their own manual gate picker.
      }
    }

    resolve().finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [user]);

  return { session, loading };
}
